import { useMemo } from 'react';
import { useApplyProvider } from 'V2/Apply';
import { PartnersEnum, PartnerConfig } from 'V2/Types/PartnerConfig';
import { PARTNER_CONFIGS } from 'constants/partnerConfigs';

const mapPartnerNameToEnum = (
  partnerName: string | undefined
): PartnersEnum => {
  if (!partnerName) {
    return 'DEFAULT';
  }

  const partnerNameMap: Record<string, PartnersEnum> = {
    quotemagic: 'QUOTE_MAGIC',
    'mt lead media quotemagic': 'QUOTE_MAGIC',
  };

  const normalizedName = partnerName.toLowerCase();
  return partnerNameMap[normalizedName] || 'DEFAULT';
};

/**
 * Hook to get partner configuration based on current partner
 * Falls back to DEFAULT config if partner is not found
 */
export const usePartnerConfig = (): PartnerConfig => {
  const {
    app: {
      status: { partner },
    },
  } = useApplyProvider();

  const partnerEnum = useMemo(
    () => mapPartnerNameToEnum(partner?.name),
    [partner?.name]
  );

  const config = useMemo(() => {
    const partnerConfig = PARTNER_CONFIGS[partnerEnum];
    const defaultConfig = PARTNER_CONFIGS.DEFAULT;

    return {
      labels: {
        nextStep:
          partnerConfig?.labels?.nextStep ?? defaultConfig.labels.nextStep,
        edit: partnerConfig?.labels?.edit ?? defaultConfig.labels.edit,
      },
      features: {
        showMiddleName:
          partnerConfig?.features?.showMiddleName ??
          defaultConfig.features.showMiddleName,
        showSuffix:
          partnerConfig?.features?.showSuffix ??
          defaultConfig.features.showSuffix,
        useYesNoAddVehicle:
          partnerConfig?.features?.useYesNoAddVehicle ??
          defaultConfig.features.useYesNoAddVehicle,
        useYesNoDriverMilitaryService:
          partnerConfig?.features?.useYesNoDriverMilitaryService ??
          defaultConfig.features.useYesNoDriverMilitaryService,
        allowUserInputAddressLookup:
          partnerConfig?.features?.allowUserInputAddressLookup ??
          defaultConfig.features.allowUserInputAddressLookup,
        showInvalidAddressModal:
          partnerConfig?.features?.showInvalidAddressModal ??
          defaultConfig.features.showInvalidAddressModal,
        showBundleQuoteRequested:
          partnerConfig?.features?.showBundleQuoteRequested ??
          defaultConfig.features.showBundleQuoteRequested,
        showContactPhone:
          partnerConfig?.features?.showContactPhone ??
          defaultConfig.features.showContactPhone,
        useVehicleYearMakeModelSplit:
          partnerConfig?.features?.useVehicleYearMakeModelSplit ??
          defaultConfig.features.useVehicleYearMakeModelSplit,
      },
    } as PartnerConfig;
  }, [partnerEnum]);

  return config;
};
